import { useEffect, useRef, useState } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import { liveChatService } from '@/services/liveChat';
import styles from '@/styles/chat.module.scss';

const ManagerIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 12c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm0 2c-3.87 0-7 2.69-7 6v2h14v-2c0-3.31-3.13-6-7-6z" />
  </svg>
);

const UserIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

/**
 * Экран живого чата с менеджером.
 * Все сообщения приходят через liveChatService (Socket.IO).
 */
export default function LiveChatScreen() {
  const { t } = useLocalization();
  const [messages, setMessages] = useState([]);
  const scrollRef = useRef(null);

  const applyHistory = (historyMessages, { includeClosedNotice = false } = {}) => {
    const closedNotice = {
      id: 'session-closed-notice',
      textKey: 'liveChat.sessionClosed',
      author: 'system',
      kind: 'system',
      ts: new Date().toISOString(),
    };

    if (historyMessages.length > 0) {
      const withoutDuplicate = historyMessages.filter(
        (message) => message.id !== closedNotice.id && message.textKey !== closedNotice.textKey,
      );
      setMessages(
        includeClosedNotice ? [...withoutDuplicate, closedNotice] : withoutDuplicate,
      );
      return;
    }

    if (includeClosedNotice) {
      setMessages([closedNotice]);
      return;
    }

    setMessages([
      {
        id: 'live-chat-connected',
        author: 'manager',
        text: t('liveChat.connected'),
      },
    ]);
  };

  useEffect(() => {
    let isMounted = true;

    const loadHistory = async () => {
      const historyMessages = await liveChatService.getHistory();
      if (!isMounted) return;
      applyHistory(historyMessages, {
        includeClosedNotice: liveChatService.needsResume(),
      });
    };

    loadHistory();

    const unsubscribe = liveChatService.subscribe((message) => {
      if (!isMounted) return;

      if (message.kind === 'history') {
        applyHistory(message.messages || [], {
          includeClosedNotice: liveChatService.needsResume(),
        });
        return;
      }

      setMessages(prev => [...prev, message]);
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [t]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [messages]);

  return (
    <div ref={scrollRef} className={styles.messages}>
      {messages.map((message) => {
        const isUser = message.author === 'user';
        const isError = message.kind === 'error';
        const isSystem = message.kind === 'system' || message.kind === 'status';
        const messageText = message.textKey
          ? t(message.textKey)
          : message.errorKey
            ? t(message.errorKey)
            : message.text;

        if (isError) {
          return (
            <div key={message.id} className={[styles.chatLine, styles.chatLineError].join(' ')}>
              <div className={styles.bubbleError}>{messageText}</div>
            </div>
          );
        }

        if (isSystem) {
          return (
            <div key={message.id} className={[styles.chatLine, styles.chatLineSystem].join(' ')}>
              <div className={styles.bubbleSystem}>{messageText}</div>
            </div>
          );
        }

        return (
          <div
            key={message.id}
            className={[
              styles.chatLine,
              isUser ? styles.chatLineUser : styles.chatLineManager,
            ].join(' ')}
          >
            {!isUser && (
              <span className={[styles.chatAvatar, styles.chatAvatarManager].join(' ')}>
                <ManagerIcon />
              </span>
            )}
            <div className={isUser ? styles.bubbleUser : styles.bubble}>
              {messageText}
            </div>
            {isUser && (
              <span className={[styles.chatAvatar, styles.chatAvatarUser].join(' ')}>
                <UserIcon />
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
